import { KEYBOARD_BINDABLES, isUnbindable, text } from '#contract'
import type { KeyboardBindable, Settings } from '#contract'
import { useEffect, useRef, useState } from 'preact/hooks'
import { useClient } from '../../app/client-context.ts'
import { useView } from '../../app/use-view.ts'
import { Button } from '../../components/button.tsx'
import { SettingRow } from '../../components/setting-row.tsx'
import { Slider } from '../../components/slider.tsx'
import { Toggle } from '../../components/toggle.tsx'
import { useT } from '../../i18n/index.ts'
import { formatSensitivity } from './format.ts'
import { formatKeyCode } from './key-label.ts'
import { KeybindRow } from './keybind-row.tsx'
import { ResetSection } from './reset-section.tsx'
import styles from './settings-screen.module.css'

type Overrides = Settings['controls']['keyboard']

const MOVED_MS = 2000

function mouseCode(button: number): string | null {
  if (button === 0) return 'MouseLeft'
  if (button === 1) return 'MouseMiddle'
  if (button === 2) return 'MouseRight'
  return null
}

/** Replaces `action` and takes `code` away from every other saved override. */
function withBinding(overrides: Overrides, action: KeyboardBindable, code: string): Overrides {
  const next: Overrides = {}
  for (const key of KEYBOARD_BINDABLES) {
    if (key === action) continue
    const codes = overrides[key]?.filter((item) => item !== code)
    if (codes !== undefined && codes.length > 0) next[key] = codes
  }
  next[action] = [code]
  return next
}

function bindingText(codes: readonly string[] | undefined, unbound: string): string {
  if (codes === undefined || codes.length === 0) return unbound
  return codes.map(formatKeyCode).join(', ')
}

/** Sensitivity, invert, and keyboard rebinding. Gamepad buttons are fixed. */
export function ControlsTab() {
  const t = useT()
  const client = useClient()
  const controls = useView((view) => view.settings.controls)
  const keyboard = useView((view) => view.input.keyboard)
  const labels = useView((view) => view.input.labels)
  const [capture, setCapture] = useState<KeyboardBindable | null>(null)
  const [errorAction, setErrorAction] = useState<KeyboardBindable | null>(null)
  const [moved, setMoved] = useState<{
    readonly action: KeyboardBindable
    readonly from: string
  } | null>(null)

  const keyboardRef = useRef(keyboard)
  const overridesRef = useRef(controls.keyboard)
  useEffect(() => {
    keyboardRef.current = keyboard
    overridesRef.current = controls.keyboard
  }, [keyboard, controls.keyboard])

  useEffect(() => {
    if (!moved) return
    const timer = window.setTimeout(() => {
      setMoved(null)
    }, MOVED_MS)
    return () => {
      window.clearTimeout(timer)
    }
  }, [moved])

  useEffect(() => {
    if (capture === null) return
    const action = capture

    const finish = (code: string): void => {
      if (isUnbindable(code)) {
        setErrorAction(action)
        setCapture(null)
        return
      }
      const from = KEYBOARD_BINDABLES.find(
        (key) => key !== action && keyboardRef.current[key].includes(code),
      )
      client?.updateSettings({
        controls: { keyboard: withBinding(overridesRef.current, action, code) },
      })
      setErrorAction(null)
      setMoved(from === undefined ? null : { action, from: t(text(`settings.binding.${from}`)) })
      setCapture(null)
    }

    const onKey = (event: KeyboardEvent): void => {
      if (event.repeat) return
      event.preventDefault()
      event.stopPropagation()
      if (event.code === 'Escape') {
        setCapture(null)
        return
      }
      finish(event.code)
    }
    const onPointer = (event: PointerEvent): void => {
      if (event.pointerType !== 'mouse') return
      const code = mouseCode(event.button)
      if (code === null) return
      event.preventDefault()
      event.stopPropagation()
      finish(code)
    }

    window.addEventListener('keydown', onKey, true)
    window.addEventListener('pointerdown', onPointer, true)
    return () => {
      window.removeEventListener('keydown', onKey, true)
      window.removeEventListener('pointerdown', onPointer, true)
    }
  }, [capture, client, t])

  const unbound = t(text('settings.controls.unbound'))
  const prompt = t(text('settings.controls.pressKey'))
  const patchSensitivity = (patch: Partial<Settings['controls']>): void => {
    client?.updateSettings({ controls: patch })
  }

  return (
    <div class={styles.stack}>
      <SettingRow label={t(text('settings.controls.mouse'))}>
        <Slider
          min={0.1}
          max={5}
          step={0.1}
          value={controls.mouseSensitivity}
          format={formatSensitivity}
          onChange={(mouseSensitivity) => {
            patchSensitivity({ mouseSensitivity })
          }}
        />
      </SettingRow>
      <SettingRow label={t(text('settings.controls.gamepad'))}>
        <Slider
          min={0.1}
          max={5}
          step={0.1}
          value={controls.gamepadSensitivity}
          format={formatSensitivity}
          onChange={(gamepadSensitivity) => {
            patchSensitivity({ gamepadSensitivity })
          }}
        />
      </SettingRow>
      <SettingRow label={t(text('settings.controls.touch'))}>
        <Slider
          min={0.1}
          max={5}
          step={0.1}
          value={controls.touchSensitivity}
          format={formatSensitivity}
          onChange={(touchSensitivity) => {
            patchSensitivity({ touchSensitivity })
          }}
        />
      </SettingRow>
      <SettingRow label={t(text('settings.controls.invertY'))}>
        <Toggle
          checked={controls.invertY}
          onChange={(invertY) => {
            patchSensitivity({ invertY })
          }}
        />
      </SettingRow>

      <h2 class={styles.heading}>{t(text('settings.controls.bindings'))}</h2>
      <div class={styles.bindings}>
        {KEYBOARD_BINDABLES.map((action) => (
          <KeybindRow
            key={action}
            label={t(text(`settings.binding.${action}`))}
            binding={bindingText(keyboard[action], unbound)}
            capturing={capture === action}
            prompt={prompt}
            error={errorAction === action ? t(text('settings.controls.reserved')) : null}
            moved={
              moved?.action === action
                ? t(text('settings.controls.moved', { action: moved.from }))
                : null
            }
            onActivate={() => {
              setErrorAction(null)
              setMoved(null)
              setCapture(action)
            }}
          />
        ))}
      </div>
      <div class={styles.reset}>
        <Button
          variant="ghost"
          onClick={() => {
            client?.updateSettings({ controls: { keyboard: {} } })
          }}
        >
          {t(text('settings.controls.resetBindings'))}
        </Button>
      </div>

      <h2 class={styles.heading}>{t(text('settings.controls.gamepadLayout'))}</h2>
      <div class={styles.gamepad}>
        <p class={styles.gamepadRow}>
          <span>{t(text('settings.binding.moveForward'))}</span>
          <span class={styles.gamepadValue}>{t(text('settings.controls.stick.move'))}</span>
        </p>
        <p class={styles.gamepadRow}>
          <span>{t(text('settings.binding.look'))}</span>
          <span class={styles.gamepadValue}>{t(text('settings.controls.stick.look'))}</span>
        </p>
        <p class={styles.gamepadRow}>
          <span>{t(text('settings.binding.jump'))}</span>
          <span class={styles.gamepadValue}>{labels.jump.gamepad}</span>
        </p>
        <p class={styles.gamepadRow}>
          <span>{t(text('settings.binding.sprint'))}</span>
          <span class={styles.gamepadValue}>{labels.sprint.gamepad}</span>
        </p>
        <p class={styles.gamepadRow}>
          <span>{t(text('settings.binding.interact'))}</span>
          <span class={styles.gamepadValue}>{labels.interact.gamepad}</span>
        </p>
        <p class={styles.gamepadRow}>
          <span>{t(text('settings.binding.skip'))}</span>
          <span class={styles.gamepadValue}>{labels.skip.gamepad}</span>
        </p>
        <p class={styles.gamepadRow}>
          <span>{t(text('settings.binding.useItem'))}</span>
          <span class={styles.gamepadValue}>{labels.useItem.gamepad}</span>
        </p>
        <p class={styles.gamepadRow}>
          <span>{t(text('settings.binding.drop'))}</span>
          <span class={styles.gamepadValue}>{labels.drop.gamepad}</span>
        </p>
        <p class={styles.gamepadRow}>
          <span>{t(text('settings.binding.inventory'))}</span>
          <span class={styles.gamepadValue}>{labels.inventory.gamepad}</span>
        </p>
        <p class={styles.gamepadRow}>
          <span>{t(text('settings.binding.phone'))}</span>
          <span class={styles.gamepadValue}>{labels.phone.gamepad}</span>
        </p>
        <p class={styles.gamepadRow}>
          <span>{t(text('settings.binding.pause'))}</span>
          <span class={styles.gamepadValue}>{labels.pause.gamepad}</span>
        </p>
      </div>
      <ResetSection section="controls" />
    </div>
  )
}
